import { useCallback, useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { SubscriptionHistoryStyled } from "../SubscriptionStyled";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { CommonTable, onSortAntd } from "components/atoms/table/AntdTable";
import { useQuery } from "react-query";
import {
  SUBSCRIPTION_TYPE,
  SubscriptionPageOutletContext,
} from "api/interfaces/subscription.interface";
import StorageBar from "components/blocks/storage/StorageBar";
import styled from "styled-components";
import { FLEX } from "styles/flex";
import { baseQueryInfo } from "utils/queryUtil";
import * as mediaQuery from "components/MediaQuery";
import {
  EventHistoryDto,
  PlanDetailSummaryDto,
} from "api/interfaces/planInterface.interface";
import { formatBytesToTB, formatNumber } from "utils/functions";
import { COLORS } from "styles/colors";
import * as eventToHistoryStyles from "../usage/eventToHistory/EventToHistoryStyled";
import {
  buildEventToHistoryColumns,
  usePlanEventHistoryQuery,
} from "../usage/eventToHistory/eventToHistoryCommon";
import { ColumnType } from "antd/es/table";
import { getPlanDetailSummary } from "api/planAPI";
import { TableColumnsType } from "antd";
import { useOutletContext } from "react-router-dom";
import {
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import SubscriptionOrgInfo from "./SubscriptionOrgInfo";
import { useMediaQuery } from "react-responsive";

export type TabType = SUBSCRIPTION_TYPE | "REQUEST";

const CloudStorageCell = ({ row }: { row: any }): JSX.Element => {
  const isDefault = useMediaQuery({ maxWidth: 1500 });
  const usedStorage = row.cloudStorage?.usedStorage ?? 0;
  const totalStorage = row.cloudStorage?.totalStorage ?? 0;

  if (isDefault) {
    return (
      <StorageCell>
        {`${formatBytesToTB(usedStorage).toFixed(0)}/${formatBytesToTB(
          totalStorage
        ).toFixed(0)} TB`}
      </StorageCell>
    );
  }

  return (
    <StorageCell>
      <StorageBar usedStorage={usedStorage} totalStorage={totalStorage} />
    </StorageCell>
  );
};

export default function SubscriptionUsageHistory(): JSX.Element {
  const breakpointLg = useMediaQuery({ maxWidth: 1500 });
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { selectedRowAccount } =
    useOutletContext<SubscriptionPageOutletContext>();

  const [queryInfo, setQueryInfo] = useState({
    ...baseQueryInfo,
    pageNum: 0,
    pageLimit: 20,
    sortType: "createdAt",
    sortDirection: SORT_DIRECTION.DESC,
  });

  const onHeaderCell = (column: ColumnType<EventHistoryDto>) => {
    return {
      onClick: () => {
        if (column.key) {
          setQueryInfo((info) => {
            return {
              ...info,
              sortType: column.key as string,
              sortDirection: onSortAntd(info.sortDirection),
              pageNum: 0,
            };
          });
        }
      },
    };
  };

  const onChangePage = useCallback(
    (page: number) => {
      setQueryInfo((prev) => ({ ...prev, pageNum: page - 1 }));
    },
    [setQueryInfo]
  );

  const onChangeType = useCallback(
    (value: string | undefined) => {
      if (value === undefined) return;
      setQueryInfo((info) => {
        return {
          ...info,
          type: value === "all" ? null : value,
          pageNum: 0,
        };
      });
    },
    [setQueryInfo]
  );

  const { data: eventHistoryData, isFetching: isEventHistoryFetching } =
    usePlanEventHistoryQuery({
      accountId: selectedRowAccount.accountId,
      queryInfo,
      enabled: !!selectedRowAccount.accountId,
    });

  const historyRows = useMemo<EventHistoryDto[]>(() => {
    if (eventHistoryData?.result && eventHistoryData.error === 0) {
      return eventHistoryData.result;
    }
    return [];
  }, [eventHistoryData]);

  const historyColumns: TableColumnsType<EventHistoryDto> = useMemo(
    () =>
      buildEventToHistoryColumns({
        selectedAccount,
        queryInfo,
        onHeaderCell,
        onChangeType,
      }),
    [buildEventToHistoryColumns, selectedAccount, queryInfo, onHeaderCell]
  );

  const { data: summaryData, isFetching: isSummaryFetching } = useQuery(
    ["getPlanDetailSummary", selectedRowAccount.accountId],
    () =>
      getPlanDetailSummary({
        accountId: selectedRowAccount.accountId,
      }),
    {
      enabled: !!selectedRowAccount.accountId,
      retry: 0,
    }
  );

  const summaryRows = useMemo<PlanDetailSummaryDto[]>(() => {
    if (summaryData?.result && summaryData.error === 0) {
      return [summaryData.result];
    }
    return [];
  }, [summaryData]);

  const summaryColumns: TableColumnsType<PlanDetailSummaryDto> = [
    {
      key: "channel",
      title: "Camera Channels",
      dataIndex: "channel",
      width: 200,
      render: (value: number) => formatNumber(value),
    },
    {
      key: "cloudStorage",
      title: "Cloud Storage",
      render: (_: unknown, row: any) => <CloudStorageCell row={row} />,
      width: breakpointLg ? 150 : undefined,
    },
    {
      key: "cpOwnerEmail",
      title: "Channel Partner",
      render: (_: unknown, row: any) => (
        <PartnerCell>
          <span className="name">{row.cpOwnerName}</span>
          {!breakpointLg && <span className="email">{row.cpOwnerEmail}</span>}
        </PartnerCell>
      ),
    },
    {
      key: "isActive",
      title: "Auto-Renewal Status",
      dataIndex: "isActive",
      width: breakpointLg ? undefined : 240,
      render: (value: boolean, row: any) => (
        <StatusCell $tone={value}>
          {value ? <FaCheckCircle /> : <FaTimesCircle />}
          <span>{value ? "Active" : "Deactive"}</span>
        </StatusCell>
      ),
    },
  ];

  return (
    <>
      <SubscriptionHistoryStyled.EuSubscriptionWrapper
        $level={selectedAccount.accountLevel}
      >
        <TableInner>
          {selectedAccount.accountLevel !== LEVEL_TYPE.EU && (
            <SubscriptionOrgInfo selectedRowAccount={selectedRowAccount} />
          )}
          <div>
            <span className="table-title">Current</span>
            <CommonTable
              columns={summaryColumns}
              dataSource={summaryRows}
              rowKey="key"
              loading={isSummaryFetching}
            />
          </div>
          <eventToHistoryStyles.TableSkin>
            <span className="table-title">History</span>
            <CommonTable
              columns={historyColumns as any}
              dataSource={historyRows}
              rowKey={(row: EventHistoryDto, idx?: number) =>
                `${row.createdAt}-${idx ?? 0}`
              }
              loading={isEventHistoryFetching}
              queryInfo={queryInfo}
              onChangePage={onChangePage}
            />
          </eventToHistoryStyles.TableSkin>
        </TableInner>
      </SubscriptionHistoryStyled.EuSubscriptionWrapper>
    </>
  );
}

const TableInner = styled(SubscriptionHistoryStyled.Inner)`
  width: 100%;

  .table-title {
    margin-bottom: 10px;
    font-weight: 500;
    display: inline-block;
    color: ${({ theme }) => theme.COLORS.WHITE};
  }
`;

const StorageCell = styled.div`
  ${FLEX.FlexStartCenter};
  gap: 10px;
  max-width: 400px;

  > button {
    flex: none;
  }
`;

const PartnerCell = styled.div`
  display: flex;
  flex-direction: column;

  span {
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .name {
    color: ${({ theme }) => theme.COLORS.WHITE};
  }
  .email {
    opacity: 0.7;
  }
`;

const StatusCell = styled.div<{ $tone: boolean }>`
  ${FLEX.FlexStartCenter};
  gap: 6px;
  color: ${({ $tone }) => ($tone ? COLORS.GREEN : COLORS.RED)};
`;
